import libraryData from './beatLibrary.json';

export interface BeatLibraryActDefinition {
  id: string;
  label: string;
  title: string;
  purpose: string;
  typicalBeats: string[];
}

export interface BeatLibraryBeat {
  id: string;
  label: string;
  description: string;
}

export interface BeatLibraryMovieAct {
  id: string;
  beats: BeatLibraryBeat[];
}

export interface BeatLibraryMovie {
  id: string;
  title: string;
  year: number;
  genre: string;
  credit: string;
  thesis: string;
  acts: BeatLibraryMovieAct[];
}

export interface BeatLibraryBeatTypeDefinition {
  id: string;
  name: string;
  actId: string;
  what: string;
  keywords: string[];
}

export interface BeatLibraryData {
  reviewedAt: string;
  method: {
    name: string;
    description: string;
    acts: BeatLibraryActDefinition[];
    beatTypes: BeatLibraryBeatTypeDefinition[];
  };
  movies: BeatLibraryMovie[];
}

function assertLibraryData(data: BeatLibraryData): void {
  const expectedActIds = data.method.acts.map((act) => act.id);
  const movieIds = new Set<string>();

  if (expectedActIds.length !== 4 || new Set(expectedActIds).size !== expectedActIds.length) {
    throw new Error('Beat Library method must define four unique acts.');
  }

  const beatTypeIds = data.method.beatTypes.map((type) => type.id);
  if (beatTypeIds.length === 0 || new Set(beatTypeIds).size !== beatTypeIds.length) {
    throw new Error('Beat Library method must define unique beat types.');
  }
  data.method.beatTypes.forEach((type) => {
    if (!expectedActIds.includes(type.actId) || type.keywords.length === 0 || !type.what.trim()) {
      throw new Error(`Beat Library beat type ${type.id} is incomplete.`);
    }
  });

  data.movies.forEach((movie) => {
    if (movieIds.has(movie.id)) {
      throw new Error(`Duplicate Beat Library movie id: ${movie.id}`);
    }
    movieIds.add(movie.id);

    const actualActIds = movie.acts.map((act) => act.id);
    if (
      actualActIds.length !== expectedActIds.length
      || actualActIds.some((id, index) => id !== expectedActIds[index])
    ) {
      throw new Error(`${movie.title} must use all four Beat Library acts in method order.`);
    }

    const beatIds = movie.acts.flatMap((act) => act.beats.map((beat) => beat.id));
    if (beatIds.length === 0 || new Set(beatIds).size !== beatIds.length) {
      throw new Error(`${movie.title} must define unique beat ids.`);
    }

    if (movie.acts.some((act) => act.beats.length === 0)) {
      throw new Error(`${movie.title} must include beats in every act.`);
    }

    if (movie.acts.some((act) => act.beats.some((beat) => !beat.label.trim() || !beat.description.trim()))) {
      throw new Error(`${movie.title} contains an incomplete beat.`);
    }
  });
}

export const beatLibraryData = libraryData as BeatLibraryData;
assertLibraryData(beatLibraryData);

export const beatLibraryMovies = beatLibraryData.movies;
export const beatLibraryMethod = beatLibraryData.method;
export const beatLibraryBeatCount = beatLibraryMovies.reduce(
  (total, movie) => total + movie.acts.reduce((movieTotal, act) => movieTotal + act.beats.length, 0),
  0,
);

export function getBeatLibraryMovie(movieId: string): BeatLibraryMovie | undefined {
  return beatLibraryMovies.find((movie) => movie.id === movieId);
}

// Canonical beats for the by-beat view come from method.beatTypes. Film
// readings use adaptable labels ("Midpoint (False Victory)", "Reversal /
// Setback"), so each canonical beat matches by keyword against the normalized
// film label, own act first.
type BeatTypeDefinition = BeatLibraryBeatTypeDefinition;

const BEAT_TYPE_DEFINITIONS = beatLibraryMethod.beatTypes;

export interface BeatLibraryBeatEntry {
  movie: BeatLibraryMovie;
  beat: BeatLibraryBeat;
}

export interface BeatLibraryBeatType {
  id: string;
  name: string;
  actId: string;
  what: string;
  entries: BeatLibraryBeatEntry[];
}

function normalizeBeatLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[-–—]/g, ' ')
    .replace(/[^a-z0-9&/ ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

interface BeatTypeMatch {
  definition: BeatTypeDefinition;
  primary: boolean;
}

function matchBeatType(label: string, actId: string): BeatTypeMatch | undefined {
  const normalized = normalizeBeatLabel(label);
  const matches = (definition: BeatTypeDefinition) =>
    definition.keywords.some((keyword) => normalized.includes(keyword));
  const definition =
    BEAT_TYPE_DEFINITIONS.find((candidate) => candidate.actId === actId && matches(candidate))
    ?? BEAT_TYPE_DEFINITIONS.find(matches);
  return definition ? { definition, primary: normalized.includes(definition.keywords[0]) } : undefined;
}

// One entry per film per beat type: the first matching beat, unless a later
// beat matches the type's primary keyword and the earlier one did not.
function buildBeatTypes(): BeatLibraryBeatType[] {
  const types = BEAT_TYPE_DEFINITIONS.map(({ keywords: _keywords, ...definition }) => ({
    ...definition,
    entries: [] as BeatLibraryBeatEntry[],
  }));
  const byId = new Map(types.map((type) => [type.id, type]));

  beatLibraryMovies.forEach((movie) => {
    const chosen = new Map<string, { beat: BeatLibraryBeat; primary: boolean }>();
    movie.acts.forEach((act) => {
      act.beats.forEach((beat) => {
        const match = matchBeatType(beat.label, act.id);
        if (!match) return;
        const existing = chosen.get(match.definition.id);
        if (!existing || (match.primary && !existing.primary)) {
          chosen.set(match.definition.id, { beat, primary: match.primary });
        }
      });
    });
    chosen.forEach(({ beat }, typeId) => byId.get(typeId)?.entries.push({ movie, beat }));
  });

  return types;
}

export const beatLibraryBeatTypes = buildBeatTypes();

export function getBeatLibraryBeatType(beatTypeId: string): BeatLibraryBeatType | undefined {
  return beatLibraryBeatTypes.find((type) => type.id === beatTypeId);
}

export function getBeatTypeForBeat(movie: BeatLibraryMovie, beat: BeatLibraryBeat): BeatLibraryBeatType | undefined {
  return beatLibraryBeatTypes.find((type) =>
    type.entries.some((entry) => entry.movie.id === movie.id && entry.beat.id === beat.id),
  );
}
